import ArrowLeftIcon from '@assets/icons/arrow-left-thin.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { MoreMenuItemIcon, MoreMenuSection } from './more-menu-item-icon';
import { ScrollView, Text, TouchableOpacity, View } from 'react-native';

const BACK_ICON_SIZE = 24;

interface Props {
  onBack: () => void;
  sections: MoreMenuSection[];
  /** Safe-area top inset — the submenu replaces the drawer header, so it
   *  owns the status-bar spacing the header used to provide. */
  topInset: number;
}

/**
 * Tablet "More" secondary screen. Replaces the drawer body in place (no
 * floating popover — that's the desktop/phone surface) with a Back affordance
 * and the same sectioned items the popup renders. Item presses run the
 * caller-supplied onPress (which navigates and closes the drawer), so the
 * drawer remounts at the top level on next open.
 */
export const MoreSubmenuList: React.FC<Props> = ({
  onBack,
  sections,
  topInset,
}) => {
  const [styles, theme] = useStyles();

  return (
    <View style={styles.container}>
      <TouchableOpacity
        accessibilityRole="button"
        onPress={onBack}
        style={[styles.backRow, { marginTop: topInset }]}
        testID="more-submenu-back"
      >
        {/* Color via `fill`: the repo's svgr config maps the icon's #000 to
            props.fill. Passing `stroke` instead lands on the root <svg> and the
            bounding-box path inherits it, drawing a square border. */}
        <ArrowLeftIcon
          fill={theme.colors.secondary}
          height={BACK_ICON_SIZE}
          width={BACK_ICON_SIZE}
        />
        <Text style={[TYPOGRAPHY.small, FONTS.primaryBold, styles.backLabel]}>
          {i18n.t('back')}
        </Text>
      </TouchableOpacity>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {sections.map((section) => (
          <View
            key={section.title ?? section.items[0]?.id ?? 'untitled'}
            style={styles.section}
          >
            {section.title ? (
              <Text
                numberOfLines={1}
                style={[
                  TYPOGRAPHY.xsmall,
                  FONTS.primaryBold,
                  styles.sectionTitle,
                ]}
              >
                {section.title}
              </Text>
            ) : null}
            {section.items.map((item) => (
              <TouchableOpacity
                key={item.id}
                onPress={item.onPress}
                style={styles.itemRow}
                testID={`more-menu-item-${item.id}`}
              >
                <MoreMenuItemIcon item={item} />
                <Text
                  numberOfLines={1}
                  style={[
                    TYPOGRAPHY.small,
                    FONTS.primaryBold,
                    styles.itemLabel,
                  ]}
                >
                  {item.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        ))}
      </ScrollView>
    </View>
  );
};

// Figma spec: rows px-24 py-12, 16px icon→label gap, section headers tertiary
// grey pt-16 pb-8.
const ROW_PADDING_HORIZONTAL = 24;
const ICON_LABEL_GAP = 16;

const useStyles = createStyles((theme) => ({
  backLabel: {
    color: theme.colors.secondary,
    marginLeft: ICON_LABEL_GAP,
  },
  backRow: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: ROW_PADDING_HORIZONTAL,
    paddingVertical: 16,
  },
  container: {
    flex: 1,
  },
  itemLabel: {
    marginLeft: ICON_LABEL_GAP,
  },
  itemRow: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: ROW_PADDING_HORIZONTAL,
    paddingVertical: 12,
  },
  scrollContent: {
    paddingTop: 8,
  },
  section: {
    paddingBottom: 8,
  },
  sectionTitle: {
    color: theme.colors.inactiveText,
    paddingBottom: 8,
    paddingHorizontal: ROW_PADDING_HORIZONTAL,
    paddingTop: 16,
  },
}));
